import { Dialog } from "@dash/dialog";
import { Button, IconButton } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { NotificationCountBadge, NotificationsPermissions } from "@dash/nav";
import { useTheme } from "@emotion/react";
import { useMemo, useRef, useState } from "react";
import { MdNotificationsNone, MdOutlineNotifications, MdOutlineSettings } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom-v5-compat";

import { getMegaraxPath } from "@megaron/megarax-uri";

import { desktopNavWidth, navAccent } from "../nav/navConstants";
import { groupSignals, isSignalGroupUnread, sortSignalGroups } from "./groupSignals";
import { SignalGroupRow } from "./SignalGroupRow";
import { unreadSignalDocIds } from "./signalReadStatus";
import { useSignals } from "./SignalsProvider";

type Props = {
  closeDrawer?: () => void;
  variant?: "default" | "pale";
  iconColor?: string;
  dialogLeft?: string;
};

const bellSignalsLimit = 5;
const signalsPath = getMegaraxPath.uri("megarax://signals");

export const SignalsBellButton: React.FunctionComponent<Props> = (props) => {
  const variant = props.variant ?? "default";
  const theme = useTheme();
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const { signals, markRead, autoMarkRead } = useSignals();

  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const outsideClickIgnoreRefs = useMemo(() => [triggerRef], []);

  const groups = useMemo(() => {
    const grouped = groupSignals(signals);
    return sortSignalGroups(grouped, { field: "createdAt", order: "DESC" }, (app) => app);
  }, [signals]);

  const groupCount = groups.filter(isSignalGroupUnread).length;

  const close = () => {
    autoMarkRead(unreadSignalDocIds(groups.slice(0, bellSignalsLimit)));
    setIsOpen(false);
  };

  const onToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isDesktop) {
      props.closeDrawer?.();
      navigate(signalsPath);
      return;
    }

    if (isOpen) {
      close();
      return;
    }

    props.closeDrawer?.();
    setIsOpen(true);
  };

  const panelAccent = variant === "pale" ? props.iconColor ?? navAccent : navAccent;

  return (
    <>
      <div
        ref={triggerRef}
        css={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {variant === "pale" ? (
          <IconButton
            css={{
              fontSize: "1.35rem",
              padding: "0.25rem",
              margin: 0,
              borderRadius: theme.smallBorderRadius,
              background: "transparent",
              color: props.iconColor ?? navAccent,
              ":hover": {
                background: "rgba(0,0,0,0.04)",
              },
            }}
            onClick={onToggle}
          >
            <MdOutlineNotifications />
          </IconButton>
        ) : (
          <Button
            icon={<MdOutlineNotifications />}
            css={{
              background: "rgba(255,255,255,0.15)",
              width: "32px",
              height: "32px",
            }}
            onClick={onToggle}
          />
        )}
        {groupCount > 0 && (
          <div
            css={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
            }}
          >
            <NotificationCountBadge
              count={groupCount}
              color={props.iconColor ?? navAccent}
            />
          </div>
        )}
      </div>

      {isOpen && (
        <Dialog
          onClose={close}
          hideHeader
          hideOverlay={isDesktop}
          zIndex={1010}
          outsideClickIgnoreRefs={outsideClickIgnoreRefs}
          background="white"
          contentCss={{
            border: `1px solid ${theme.colors.skeleton}`,
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            ...(isDesktop && {
              position: "fixed",
              margin: 0,
              width: "400px",
              top: "8px",
              left: props.dialogLeft ?? `calc(${desktopNavWidth} + 8px)`,
            }),
          }}
          placement={isDesktop ? "center" : "top"}
        >
          <div
            css={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              marginBottom: "12px",
              color: panelAccent,
            }}
          >
            <MdOutlineNotifications
              css={{ width: "1.5rem", height: "1.5rem", flexShrink: 0, fill: panelAccent }}
            />
            <span css={{ fontWeight: 700, fontSize: "1.25rem", flexShrink: 0 }}>Powiadomienia</span>
            <div css={{ height: "1px", width: "100%", backgroundColor: theme.colors.skeleton, border: "none" }} />
            <MdOutlineSettings
              css={{
                width: "1.25rem",
                height: "1.25rem",
                flexShrink: 0,
                fill: panelAccent,
                cursor: "pointer",
              }}
              onClick={() => setSettingsOpen(true)}
            />
          </div>

          {groups.length > 0 ? (
            <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.125rem",
                  maxHeight: "750px",
                  overflowY: "auto",
                }}
              >
                {groups.slice(0, bellSignalsLimit).map((group) => (
                  <SignalGroupRow
                    key={group.key}
                    group={group}
                    onNavigate={close}
                    onMarkRead={(nextGroup) => markRead(nextGroup.signals.map((signal) => signal.docId))}
                  />
                ))}
              </div>
              <Link
                to={signalsPath}
                onClick={close}
                css={{
                  flexShrink: 0,
                  fontSize: "0.8125rem",
                  padding: "0.375rem 0.5rem",
                  color: navAccent,
                  opacity: 0.7,
                  "&:hover": { opacity: 1 },
                }}
              >
                Zobacz wszystkie powiadomienia ({groups.length})
              </Link>
            </div>
          ) : (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                borderRadius: theme.smallBorderRadius,
                background: theme.colors.skeleton,
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                gap: "8px",
                color: theme.colors.secondaryText,
                height: "104px",
                width: "100%",
              }}
            >
              <MdNotificationsNone css={{ fontSize: "40px" }} />
              <span css={{ fontSize: "14px" }}>Brak powiadomień</span>
            </div>
          )}
        </Dialog>
      )}

      {settingsOpen && (
        <Dialog onClose={() => setSettingsOpen(false)} header="Ustawienia powiadomień" css={{ width: "400px" }}>
          <NotificationsPermissions notificationsQueryKey="notifications" hideAppChannel />
        </Dialog>
      )}
    </>
  );
};
